import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { EmailDailyReport } from "../../api/compute/EmailDailyReport";
import { modalStyle, modalStyleLog, prepareAccountDataToUi } from "./utils";
import { CardLog } from "./widgets/CardLog";
import InfoOverview from "./widgets/InfoOverview";
import { ModalTitle } from "./widgets/ModalTitle";
import { useAuth } from "../../hooks/useAuth";

export default function DailyReport() {
  const [mailVersion, setMailVersion] = useState<"1" | "2">("1");
  const [selectedAccount, setSelectedAccount] = useState<any>(
    "dev@trendrating.net"
  );
  const [content, setContent] = useState<any>(null);
  const [accounts, setAccounts] = useState<any>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [subject, setSubject] = useState("Trendrating Daily Report");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const email = new EmailDailyReport();
    email.getAccounts().then((res) => setAccounts(prepareAccountDataToUi(res)));
  }, []);

  const userData = useAuth();
  // console.log("userData", userData.user);

  useEffect(() => {
    setIsPreviewLoading(true);
    fetch(`/api/daily-report/v${mailVersion}.0/preview/${selectedAccount}/`, {
      method: "POST",
      body: JSON.stringify({
        additionalCookies: userData?.user?.additionalCookies,
      }),
    }).then((res) => {
      res.text().then((resp) => {
        setContent(resp);
        setIsPreviewLoading(false);
      });
    });
  }, [mailVersion, selectedAccount, userData?.user?.additionalCookies]);

  const [showModal, setShowModal] = useState(false);
  const [isPreSendLoading, setIsPreSendLoading] = useState(false);
  const [overviewInfo, setOverviewInfo] = useState<any>(null);
  const [overviewType, setOverviewType] = useState<"END" | "START">("START");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    if (showModal === false) {
      setOverviewInfo(null);
      setOverviewType("START");
      setIsSendingEmail(false);
    }
  }, [showModal]);
  const sendHandler = useCallback(
    (debugMode: boolean) => {
      setIsPreSendLoading(true);
      const email = new EmailDailyReport();
      let response = email.preSend({
        additionalCookies: userData.user.additionalCookies,
      });
      response.then(
        (response) => {
          setOverviewInfo(response);
          setIsPreSendLoading(false);
        },
        (err) => console.log(err)
      );
      setShowModal(true);
      setDebugMode(debugMode);
    },
    [userData?.user?.additionalCookies]
  );

  const sendExecute = useCallback(
    (debug) => {
      setIsSendingEmail(true);
      const params = {
        message: message,
        subject: subject,
        additionalCookies: userData?.user?.additionalCookies,
        debug,
      };
      const email = new EmailDailyReport();
      const success = (resp) => {
        setOverviewInfo(resp);
        setOverviewType("END");
        setIsSendingEmail(false);
      };
      const error = (resp) => {
        console.log("error", resp);
        setIsSendingEmail(false);
      };
      let response = email.send(params);
      response.then(success, error);
    },
    [message, subject, userData?.user?.additionalCookies]
  );

  return (
    <Box display={"flex"} gap={2} height={"100%"} width={"100%"}>
      <Box width={"50%"} height={"100%"}>
        <Card sx={{ boxShadow: 3, height: "100%", display: "flex" }}>
          <CardContent
            sx={{
              maxHeight: "100%",
              position: "relative",
              pb: "16px !important",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box gap={2} display={"flex"}>
              <Button
                onClick={() => setMailVersion("1")}
                color={mailVersion === "1" ? "secondary" : "primary"}
              >
                Preview Daily Report V1
              </Button>
              <Button
                onClick={() => setMailVersion("2")}
                color={mailVersion === "2" ? "secondary" : "primary"}
              >
                Preview Daily Report V2
              </Button>
            </Box>

            {isPreviewLoading && (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  border: "2px black solid",
                  borderRadius: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="secondary" />
              </div>
            )}

            {!isPreviewLoading && (
              <div
                dangerouslySetInnerHTML={{ __html: content }}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "2px black solid",
                  borderRadius: 4,
                  overflow: "auto",
                }}
              ></div>
            )}
          </CardContent>
        </Card>
      </Box>
      <Box
        width={"50%"}
        display={"flex"}
        flexDirection={"column"}
        gap={2}
        height={"100%"}
      >
        <Card sx={{ boxShadow: 3, height: "100%" }}>
          <CardContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Card>
              <CardContent>
                <Box display={"flex"} gap={2} flexDirection={"column"}>
                  <Typography>Preview account</Typography>
                  <Select
                    size="small"
                    onChange={(e) => {
                      setSelectedAccount(e.target.value);
                    }}
                    value={selectedAccount}
                  >
                    {accounts.map((account, index) => (
                      <MenuItem key={index} value={account.value}>
                        <Typography>{account.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display={"flex"} gap={2} flexDirection={"column"}>
                  <Typography>Subject</Typography>
                  <TextField
                    onChange={(e) => setSubject(e.target.value)}
                    value={subject}
                  ></TextField>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display={"flex"} gap={2} flexDirection={"column"}>
                  <Typography>Message</Typography>
                  <TextField
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                    type={"text"}
                    multiline
                    rows={5}
                  ></TextField>
                </Box>
              </CardContent>
            </Card>
            <Box display={"flex"} justifyContent={"center"} gap={1}>
              <Box>
                <Button color="secondary" onClick={() => sendHandler(false)}>
                  Send
                </Button>
              </Box>
              <Box>
                <Button color="info" onClick={() => sendHandler(true)}>
                  Test
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      {showModal && (
        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <Box sx={modalStyle}>
            {overviewInfo != null ? (
              <>
                <ModalTitle
                  overviewType={overviewType}
                  overviewInfo={overviewInfo}
                />
                <InfoOverview
                  isSending={isSendingEmail}
                  type={overviewType}
                  info={overviewInfo.v1}
                  title="Daily report v1.0"
                />
                <InfoOverview
                  isSending={isSendingEmail}
                  type={overviewType}
                  info={overviewInfo.v2}
                  title="Daily report v2.0"
                />
                {showLog && (
                  <Modal open={showModal} onClose={() => setShowLog(false)}>
                    <Box sx={modalStyleLog}>
                      <CardLog title={"V1"} data={overviewInfo["v1"]} />
                      <CardLog title={"V2"} data={overviewInfo["v2"]} />
                    </Box>
                  </Modal>
                )}

                <Box display={"flex"} justifyContent={"end"} gap={2}>
                  {overviewType === "END" && (
                    <Button onClick={() => setShowLog(true)} color="secondary">
                      Show log
                    </Button>
                  )}
                  <Button onClick={() => setShowModal(false)}>Cancel</Button>
                  {overviewType !== "END" && debugMode === false && (
                    <Button
                      disabled={isSendingEmail}
                      color="secondary"
                      onClick={() => sendExecute(false)}
                    >
                      Send
                    </Button>
                  )}
                  {overviewType !== "END" && debugMode === true && (
                    <Button
                      disabled={isSendingEmail}
                      color="secondary"
                      onClick={() => sendExecute(true)}
                    >
                      Test
                    </Button>
                  )}
                </Box>
              </>
            ) : null}

            {isPreSendLoading && <CircularProgress color="secondary" />}
          </Box>
        </Modal>
      )}
    </Box>
  );
}
